<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { HelpCircle, X, Keyboard, Sparkles } from 'lucide-vue-next';

/**
 * Help modal — keyboard shortcuts + tips.
 * Otwierany przez "?" lub Shift+/ albo przycisk w UI.
 */

const visible = ref(false);
const isMac = ref(false);

const mod = computed(() => (isMac.value ? '⌘' : 'Ctrl'));

const shortcuts = computed(() => [
  {
    group: 'Globalne',
    items: [
      { keys: [`${mod.value}`, 'K'], desc: 'Otwórz Command Palette (szukaj wszystkiego)' },
      { keys: ['?'], desc: 'Pokaż tę pomoc' },
      { keys: ['Esc'], desc: 'Zamknij otwarte modale / slide-overy' },
    ],
  },
  {
    group: 'Nawigacja',
    items: [
      { keys: ['G', 'D'], desc: 'Skok do Pulpitu' },
      { keys: ['G', 'P'], desc: 'Skok do Pipeline' },
      { keys: ['G', 'S'], desc: 'Skok do Stocku' },
      { keys: ['G', 'R'], desc: 'Skok do Raportów' },
    ],
  },
  {
    group: 'Pipeline',
    items: [
      { keys: ['Drag'], desc: 'Przeciągnij lead między etapami (touch + desktop)' },
      { keys: ['Tap'], desc: 'Otwórz szczegóły leada' },
    ],
  },
  {
    group: 'Sprzedaż',
    items: [
      { keys: ['Mic'], desc: 'Wpisz głosem — "Sprzedałem Audi A6 za 365 tysięcy Kowalskiemu"' },
      { keys: ['Enter'], desc: 'Zapisz quick entry' },
    ],
  },
]);

function onKey(e: KeyboardEvent) {
  // "?" = Shift+/ na większości klawiatur
  if (e.key === '?' && !visible.value) {
    // Nie blokuj jeśli user wpisuje tekst w input/textarea
    const target = e.target as HTMLElement | null;
    if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable)
      return;
    e.preventDefault();
    visible.value = true;
  } else if (e.key === 'Escape' && visible.value) {
    visible.value = false;
  }
}

onMounted(() => {
  isMac.value = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);
  window.addEventListener('keydown', onKey);
});
onUnmounted(() => {
  window.removeEventListener('keydown', onKey);
});

defineExpose({
  open() {
    visible.value = true;
  },
});
</script>

<template>
  <Teleport to="body">
    <Transition name="help-fade">
      <div
        v-if="visible"
        class="fixed inset-0 z-[200] flex items-center justify-center px-4"
        :style="{ background: 'rgba(0, 0, 0, 0.55)', backdropFilter: 'blur(6px)' }"
        @click.self="visible = false"
      >
        <div
          class="card card-featured w-full max-w-lg p-6 relative animate-fade-in-up max-h-[85vh] overflow-y-auto"
          @click.stop
        >
          <button
            type="button"
            class="absolute top-3 right-3 btn btn-icon btn-ghost"
            aria-label="Zamknij"
            @click="visible = false"
          >
            <X :size="16" />
          </button>

          <header class="flex items-center gap-3 mb-5">
            <div
              class="w-12 h-12 rounded-full flex items-center justify-center"
              :style="{
                background: 'color-mix(in srgb, var(--color-accent) 16%, transparent)',
                color: 'var(--color-accent)',
              }"
            >
              <Keyboard :size="22" />
            </div>
            <div>
              <p class="text-caps mb-0.5"><Sparkles :size="11" class="inline -mt-0.5" /> Pomoc</p>
              <h2 class="text-xl font-bold">Skroty klawiszowe</h2>
            </div>
          </header>

          <div class="space-y-5">
            <section v-for="group in shortcuts" :key="group.group" class="space-y-2">
              <h3 class="text-caps">{{ group.group }}</h3>
              <ul class="space-y-1.5">
                <li
                  v-for="item in group.items"
                  :key="item.desc"
                  class="flex items-center justify-between gap-3 text-sm"
                >
                  <span :style="{ color: 'var(--surface-fg-2)' }">{{ item.desc }}</span>
                  <span class="flex items-center gap-1 shrink-0">
                    <kbd
                      v-for="key in item.keys"
                      :key="key"
                      class="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono"
                      :style="{
                        background: 'var(--card-bg-glass)',
                        border: '1px solid var(--border-soft)',
                        color: 'var(--surface-fg)',
                      }"
                    >
                      {{ key }}
                    </kbd>
                  </span>
                </li>
              </ul>
            </section>
          </div>

          <footer
            class="mt-6 pt-4 text-xs"
            :style="{ borderTop: '1px solid var(--border-soft)', color: 'var(--surface-fg-faint)' }"
          >
            <HelpCircle :size="11" class="inline -mt-0.5" />
            Wsparcie:
            <a
              href="mailto:office@domoresoft.com"
              class="hover:underline"
              :style="{ color: 'var(--color-accent)' }"
            >
              office@domoresoft.com
            </a>
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.help-fade-enter-active,
.help-fade-leave-active {
  transition: opacity 0.2s var(--ease-smooth);
}
.help-fade-enter-from,
.help-fade-leave-to {
  opacity: 0;
}

kbd {
  font-family: ui-monospace, monospace;
}
</style>
